/**
 * Saved styles. Off unless the frontend build was given
 * REACT_APP_STYLE_ROOM=true. The backend switch is STYLE_ROOM_ENABLED.
 */
import { STYLE_PRESETS } from './stylePresets';
import {
  BACK_ON_BOOK_STYLE,
  BOOK_STYLE,
  THIS_CHAPTER,
  THIS_SCENE,
  USED_IN_EVERY_SCENE,
  USED_IN_SCENES,
  WRITTEN_WITH,
} from './writerCopy';

export const SAMPLE_WORD_CAP = 150;

export function styleRoomEnabled() {
  return process.env.REACT_APP_STYLE_ROOM === 'true';
}

export function foldStyleNotes(card) {
  const parts = ['tone', 'mechanics', 'lexicon', 'direction_note']
    .map((key) => String(card?.[key] || '').trim())
    .filter(Boolean);
  const current = String(card?.resolved_text || '').trim();
  if (!parts.length) return { text: current, moved: false };
  const extra = parts.filter((part) => !current.includes(part));
  if (!extra.length) return { text: current, moved: false };
  const text = current ? `${current}\n\n${extra.join('\n\n')}` : extra.join('\n\n');
  return { text, moved: true };
}

export function capWords(text, limit = SAMPLE_WORD_CAP) {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (words.length <= limit) return String(text || '').trim();
  return words.slice(0, limit).join(' ');
}

export function styleWordCount(box, sample) {
  return `${box || ''} ${sample || ''}`.trim().split(/\s+/).filter(Boolean).length;
}

export function countWords(text) {
  return String(text || '').trim().split(/\s+/).filter(Boolean).length;
}

/** Keep the writer's typing while it is within the cap. A longer paste is cut. */
export function clampWords(text, limit = SAMPLE_WORD_CAP) {
  const raw = String(text ?? '');
  const words = raw.trim().split(/\s+/).filter(Boolean);
  if (words.length <= limit) return raw;
  return words.slice(0, limit).join(' ');
}

const DIAL_KEYS = ['closeness', 'pace', 'sentences', 'words'];

/**
 * Total stop-distance across the four dials. The quiet note shows at
 * this total or above. One dial can move at most 4 stops (1 through 5),
 * so 6 means the dials have moved on more than one stop, or one has
 * moved the full way and another has moved as well. An unset dial
 * adds nothing: it is not a chosen stop.
 */
export const DIAL_DRIFT_STOPS = 6;

export function dialDriftStops(dials, presetDials) {
  return DIAL_KEYS.reduce((total, key) => {
    const from = Number(presetDials?.[key]);
    const to = Number(dials?.[key]);
    if (!(from >= 1 && from <= 5) || !(to >= 1 && to <= 5)) return total;
    return total + Math.abs(to - from);
  }, 0);
}

export function dialsFarFromPreset(dials, presetDials) {
  return dialDriftStops(dials, presetDials) >= DIAL_DRIFT_STOPS;
}

/** A style does not store which preset it came from. Match the description. */
export function presetMatchingDescription(text, presets = STYLE_PRESETS) {
  const current = String(text || '').trim();
  if (!current) return null;
  return presets.find((preset) => String(preset.description || '').trim() === current) || null;
}

export function dialsAreSet(dials) {
  if (!dials || typeof dials !== 'object') return false;
  return DIAL_KEYS.some((key) => {
    const stop = Number(dials[key]);
    return stop >= 1 && stop <= 5;
  });
}

export function styleHasInstruction({ text = '', sample = '', dials } = {}) {
  return Boolean(
    String(text || '').trim()
    || String(sample || '').trim()
    || dialsAreSet(dials)
  );
}

/** Menu rows from modelsApi.getAvailable(), the same names the model picker shows. */
export function modelMenuNames(menu) {
  const names = {};
  [menu?.default, menu?.added].forEach((group) => {
    (group || []).forEach((model) => {
      const id = String(model?.id || '').trim();
      const name = String(model?.display_name || '').trim();
      if (id && name) names[id] = name;
    });
  });
  return names;
}

export function modelDisplayName(modelId, namesById) {
  const raw = String(modelId || '').trim();
  if (!raw) return '';
  const fromMenu = namesById && namesById[raw];
  if (fromMenu) return fromMenu;
  const slug = raw.includes('/') ? raw.slice(raw.lastIndexOf('/') + 1) : raw;
  return slug.split('-').filter(Boolean).map((part) => {
    if (/^\d/.test(part)) return part;
    if (part.length <= 3 && part === part.toLowerCase()) return part.toUpperCase();
    return part.charAt(0).toUpperCase() + part.slice(1);
  }).join(' ');
}

export function writtenWithLine(pass, namesById) {
  const styleName = String(pass?.style_name || '').trim();
  const modelName = modelDisplayName(pass?.model_id, namesById);
  return WRITTEN_WITH(styleName, modelName);
}

export function styleSourceLabel(source) {
  if (source === 'scene') return THIS_SCENE;
  if (source === 'chapter') return THIS_CHAPTER;
  if (source === 'book') return BOOK_STYLE;
  return '';
}

const SAMPLE_MARK = /(?:^|\n)[ \t]*(?:sample|exemplars)[ \t]*:[ \t]*/i;
const SAMPLE_LEAD = /^(?:sample|exemplars)[ \t]*:[ \t]*/i;

/**
 * Description and sample for the scene-writing style box.
 * A stored sample wins. A raw SAMPLE: / Exemplars: block in the
 * description is peeled off so it is not shown as plain text.
 */
export function splitStyleSample(text, exemplars = '') {
  const raw = String(text || '').replace(/\r\n/g, '\n').trim();
  const stored = String(exemplars || '').trim().replace(SAMPLE_LEAD, '');
  const at = raw.search(SAMPLE_MARK);
  if (at < 0) return { description: raw, sample: stored };
  const description = raw.slice(0, at).trim();
  const embedded = raw.slice(at).replace(SAMPLE_MARK, '').trim();
  return { description, sample: stored || embedded };
}

export function usedInLabel(count, isBookStyle) {
  if (isBookStyle) return USED_IN_EVERY_SCENE;
  const n = Number(count) || 0;
  return USED_IN_SCENES(n);
}

export function deleteStyleWarning({
  name,
  sceneCount = 0,
  chapterCount = 0,
  isBookStyle = false,
} = {}) {
  const bits = [];
  if (sceneCount === 1) bits.push(`1 scene uses ${name}`);
  else if (sceneCount > 1) bits.push(`${sceneCount} scenes use ${name}`);
  if (chapterCount === 1) bits.push(`1 chapter uses ${name}`);
  else if (chapterCount > 1) bits.push(`${chapterCount} chapters use ${name}`);
  let text = bits.length ? `${bits.join('. ')}. ` : '';
  if (sceneCount || chapterCount) text += `${BACK_ON_BOOK_STYLE} `;
  if (isBookStyle) {
    text += `${name} is the book style. Scenes that follow it will have no book style.`;
  }
  return text.trim() || `Delete ${name}?`;
}
